import { useEffect, useState } from 'react';
import { CaretDown } from '@phosphor-icons/react';
import { useSceneStore } from '../../stores/sceneStore';
import { getSpineManager } from '../../lib/spine-manager';
import { api } from '../../lib/api-client';
import type { Asset } from '@spine-workspace/shared';

/**
 * Floating HUD anchored to the bottom-left of the canvas.
 * Shown when a spine layer is selected — provides quick skin + animation (track 0) pickers.
 */
export default function CanvasLayerHUD() {
  const { selectedLayer } = useSceneStore();
  const layer = selectedLayer();
  const { setLayerSkin, setLayerAnimation } = useSceneStore();
  const [asset, setAsset] = useState<Asset | null>(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!layer || layer.layerType === 'image') { setAsset(null); return; }
    api.get<{ asset: Asset }>(`/api/assets/${layer.assetId}`)
      .then(({ asset }) => setAsset(asset))
      .catch(() => setAsset(null));
  }, [layer?.assetId]);

  if (!layer || layer.layerType === 'image' || !asset) return null;

  const skins = asset.metadata?.skins ?? [];
  const animations = asset.metadata?.animations ?? [];
  const currentAnim = layer.animation.tracks.find((t) => t.trackIndex === 0)?.name ?? '';

  if (skins.length === 0 && animations.length === 0) return null;

  function handleSkin(skin: string) {
    if (!layer) return;
    setLayerSkin(layer.id, skin);
    getSpineManager().setSkin(layer.id, skin);
  }

  function handleAnim(animName: string) {
    if (!layer) return;
    const track = layer.animation.tracks.find((t) => t.trackIndex === 0);
    const loop = track?.loop ?? true;
    const timeScale = track?.timeScale ?? 1;
    setLayerAnimation(layer.id, 0, animName, loop);
    getSpineManager().setAnimation(layer.id, 0, animName, loop, timeScale);
  }

  return (
    // left-[28px] = 20px ruler + 8px gap so the panel never overlaps the vertical ruler
    <div className="absolute bottom-3 left-[28px] z-20 pointer-events-auto">
      <div className="bg-bg-elevated/90 backdrop-blur-sm border border-border rounded-lg shadow-lg overflow-hidden">

        {/* Single-row layout */}
        <div className="flex items-center gap-1 px-2 py-1.5">

          {/* Layer name + collapse toggle */}
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="flex items-center gap-1.5 hover:bg-bg-hover/50 rounded px-1 py-0.5 transition-colors shrink-0"
          >
            <span className="font-sans text-[10px] font-semibold text-text-tertiary uppercase tracking-widest max-w-[80px] truncate">
              {layer.name}
            </span>
            <CaretDown
              size={9}
              className={`text-text-tertiary shrink-0 transition-transform duration-fast ${collapsed ? '-rotate-90' : ''}`}
            />
          </button>

          {/* Pickers — hidden when collapsed */}
          {!collapsed && (
            <>
              {skins.length > 0 && (
                <>
                  <div className="w-px h-4 bg-border-subtle shrink-0" />
                  <span className="font-sans text-[10px] text-text-tertiary shrink-0">Skin</span>
                  <select
                    value={layer.skin ?? skins[0]}
                    onChange={(e) => handleSkin(e.target.value)}
                    className="bg-bg-input border border-border text-text-primary rounded px-1.5 py-[2px] text-[11px] font-sans
                               focus:outline-none focus:border-accent transition-all duration-fast cursor-pointer max-w-[110px]"
                  >
                    {skins.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </>
              )}

              {animations.length > 0 && (
                <>
                  <div className="w-px h-4 bg-border-subtle shrink-0" />
                  <span className="font-sans text-[10px] text-text-tertiary shrink-0">Anim</span>
                  <select
                    value={currentAnim}
                    onChange={(e) => handleAnim(e.target.value)}
                    className="bg-bg-input border border-border text-text-primary rounded px-1.5 py-[2px] text-[11px] font-sans
                               focus:outline-none focus:border-accent transition-all duration-fast cursor-pointer max-w-[110px]"
                  >
                    {animations.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
